<template>
    <div 
        class="detail-page-header" 
        :class="{ 
            'detail-page-header--draggable': draggable,
            'detail-page-header--modal': isModal,
            [`detail-page-header--${colorVariant}`]: colorVariant
        }"
    >
        <div 
            class="detail-page-header__content" 
            :class="{ 'detail-page-header__content--modal': isModal }"
        >
            <!-- Left Section: Back Button + Title + Badges -->
            <div class="detail-page-header__left">
                <!-- Back Button -->
                <button 
                    v-if="showBackButton"
                    @click="$emit('back')"
                    class="detail-page-header__back-button"
                    :aria-label="backButtonLabel"
                >
                    <theme-icon icon="chevron-left" type="icons" class="detail-page-header__back-icon" />
                </button>

                <!-- Title Section -->
                <div 
                    v-if="titleClickable && !isModal"
                    @click="$emit('title-click')" 
                    class="detail-page-header__title-group detail-page-header__title-group--clickable"
                    role="button"
                    tabindex="0"
                    @keydown.enter="$emit('title-click')"
                    @keydown.space.prevent="$emit('title-click')"
                    :aria-label="titleClickLabel"
                >
                    <div class="detail-page-header__title-wrapper">
                        <h1 class="detail-page-header__title">
                            {{ title }}
                        </h1>
                        <theme-icon 
                            v-if="showTitleChevron" 
                            icon="chevron-down" 
                            type="icons" 
                            class="detail-page-header__chevron" 
                        />
                    </div>
                    <div v-if="subtitle" class="detail-page-header__subtitle">
                        {{ subtitle }}
                    </div>
                </div>
                
                <div v-else class="detail-page-header__title-group">
                    <h1 
                        class="detail-page-header__title" 
                        :class="{ 'detail-page-header__title--large': isModal }"
                    >
                        {{ title }}
                    </h1>
                    <div 
                        v-if="subtitle" 
                        class="detail-page-header__subtitle" 
                        :class="{ 'detail-page-header__subtitle--large': isModal }"
                    >
                        {{ subtitle }}
                    </div>
                </div>
                
                <!-- Badges -->
                <div v-if="badges.length > 0" class="detail-page-header__badges">
                    <slot name="badges">
                        <div
                            v-for="(badge, index) in badges"
                            :key="index"
                            :class="['detail-page-header__badge', badge.class]"
                            :title="badge.title"
                            :role="badge.role || 'status'"
                            :aria-label="badge.ariaLabel"
                        >
                            <theme-icon 
                                v-if="badge.icon" 
                                :icon="badge.icon" 
                                type="icons" 
                            />
                            <span v-if="badge.text">{{ badge.text }}</span>
                        </div>
                    </slot>
                </div>
            </div>
            
            <!-- Right Section: Actions + Close Button -->
            <div class="detail-page-header__right">
                <!-- Custom Actions Slot -->
                <slot name="actions"></slot>
                
                <!-- Close Button (for modals) -->
                <button 
                    v-if="showCloseButton" 
                    @click="$emit('close')"
                    class="detail-page-header__close-button"
                    :aria-label="closeButtonLabel"
                >
                    <theme-icon icon="close" type="icons" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DetailPageHeader',
    
    props: {
        // Title configuration
        title: {
            type: String,
            required: true
        },
        
        subtitle: {
            type: String,
            default: ''
        },
        
        titleClickable: {
            type: Boolean,
            default: false
        },
        
        showTitleChevron: {
            type: Boolean,
            default: true
        },
        
        titleClickLabel: {
            type: String,
            default: 'View all items'
        },
        
        // Back button configuration
        showBackButton: {
            type: Boolean,
            default: false
        },
        
        backButtonLabel: {
            type: String,
            default: 'Go back'
        },
        
        // Badges configuration
        badges: {
            type: Array,
            default: () => []
            // Expected format:
            // [
            //   {
            //     text: 'Badge Text',
            //     icon: 'icon-name',
            //     class: 'custom-class',
            //     title: 'Tooltip text',
            //     role: 'status',
            //     ariaLabel: 'Accessible label'
            //   }
            // ]
        },
        
        // Close button configuration
        showCloseButton: {
            type: Boolean,
            default: false
        },
        
        closeButtonLabel: {
            type: String,
            default: 'Close panel'
        },
        
        // Layout configuration
        isModal: {
            type: Boolean,
            default: false
        },
        
        draggable: {
            type: Boolean,
            default: false
        },
        
        // Color variant
        colorVariant: {
            type: String,
            default: 'blue',
            validator: (value) => ['blue', 'green'].includes(value)
        }
    }
}
</script>
